import twilioCodes from "./twilioErrorCodeDescriptions";

/*
This is scraped from the Bandwidth website on 2023-08-17
https://dev.bandwidth.com/docs/messaging/errors/

Using this script:

```
const codes = {};

Array.from(document.querySelectorAll('tr')).map(tr => {
  const tds = Array.from(tr.querySelectorAll('td'));
  if (tds.length !== 5) {
    return null;
  }

  return {
    code: tds[0].innerText,
    name: tds[2].innerText,
    description: tds[3].innerText,
  }
}).filter(r => !!r).forEach(r => {
  codes[r.code] = r;
})

console.log(JSON.stringify(codes, null, 2));
```

We'll need something more complex once we have both bandwidth and telnyx in
the mix.

*/
const codes: {
  [code: string]: {
    code: string;
    name: string;
    description: string;
  };
} = {
  "4001": {
    code: "4001",
    name: "Message was rejected for reasons other than those covered by other 4xxx codes",
    description:
      "This is a general error that the service you are attempting to use is not allowed; you may have inaccurate permissions, formatting or may not be enable to use that service.",
  },
  "4301": {
    code: "4301",
    name: "Malformed message encoding",
    description:
      "The message contains invalid characters that are not supported. Bandwidth cannot re-encode message for destination.",
  },
  "4302": {
    code: "4302",
    name: "Malformed From number",
    description:
      "The From number associated with the message is a number not routable to a carrier or valid in the industry (Ex: a 9 digit number).",
  },
  "4303": {
    code: "4303",
    name: "Malformed To Number",
    description:
      "The To number associated with the message is a number not routable to a carrier or valid in the industry (Ex: a 9 digit number).",
  },
  "4350": {
    code: "4350",
    name: "Malformed message encoding",
    description:
      "Message passed validation on receive stage, but failed on send. This is likely because the destination number (To) is an invalid number.",
  },
  "4360": {
    code: "4360",
    name: "Message expired",
    description:
      "Message was not sent because the specified expiration date passed before the message was able to send",
  },
  "4401": {
    code: "4401",
    name: "BW is unable to route the message",
    description:
      "Message is unable to be routed within Bandwidth particularly when the source and destination are the same number. The destination or To number is mis-provisioned or there is a configuration with the message that is causing a situation where a message is being sent repeatedly between the same numbers.",
  },
  "4403": {
    code: "4403",
    name: "Messaging forbidden on From number",
    description:
      "Messaging on this From number is forbidden most commonly because the number does not belong to BW or the account. Other reasons include: the TN is not enabled in the Bandwidth Dashboard, the account associated with this number is not enabled for this type of messaging, the TN is disconnected, or it is an invalid number (i.e., 11111111111).",
  },
  "4404": {
    code: "4404",
    name: "Messaging forbidden on To number",
    description:
      "Messaging on this To number is forbidden. This could be the number is not active, not enabled for messaging or is an invalid number (i.e. 11111111111)",
  },
  "4405": {
    code: "4405",
    name: "Unallocated from number",
    description:
      "The From telephone number is considered unallocated when the number does not exist in our database as an active number. This number is either not enabled for messaging at the industry level, or the number is not yet released in the industry",
  },
  "4406": {
    code: "4406",
    name: "Unallocated to number",
    description:
      "The To number associated with this message, while a valid North American number, is not yet assigned to a carrier and the message cannot be sent downstream.",
  },
  "4407": {
    code: "4407",
    name: "From Number is associated with account",
    description:
      "Undefined source account id. The From number associated with this message is not associated with this account, is an invalid number or not configured appropriately to send messages.",
  },
  "4408": {
    code: "4408",
    name: "To Number not associated with account",
    description:
      "Undefined destination account id. The To (destination) number is not associated with an account, is an invalid number or not configured correctly to receive messages.",
  },
  "4409": {
    code: "4409",
    name: "Invalid destination profile",
    description:
      "Bandwidth failed to create destination. The destination profile is considered invalid, most often this is because the destination number does not support MMS.",
  },
  "4410": {
    code: "4410",
    name: "Could not download media",
    description:
      "There was an error retrieving the media from the media web server. Check the media URL and try to access directly to see if the media can be fetched successfully.",
  },
  "4411": {
    code: "4411",
    name: "Combined size of media too large",
    description:
      "The total size of MMS message media/attachments exceeded the max file size supported",
  },
  "4412": {
    code: "4412",
    name: "Failed to parse Content-Type for media",
    description:
      "The media content type is not a supported media content type.",
  },
  "4420": {
    code: "4420",
    name: "No Route to Destination Carrier",
    description:
      "The upstream carrier associated with the message does not exist in Bandwidth configuration",
  },
  "4421": {
    code: "4421",
    name: "No Route to Destination Carrier",
    description:
      "The message cannot be sent downstream as the account associated with the message does not have permission to send to this destination. You may not be provisioned to send to this destination.",
  },
  "4431": {
    code: "4431",
    name: "Messaging on shortcode forbidden",
    description:
      "The message cannot be sent as the account associated with the message is not provisioned for Short code messaging",
  },
  "4432": {
    code: "4432",
    name: "Messaging to country forbidden",
    description:
      "Bandwidth system indicates the account associated with the message is not enabled for messaging this zone, this country or this country is outside of messaging reach (specifically for MMS).",
  },
  "4433": {
    code: "4433",
    name: "Messaging on Toll Free Number Forbidden",
    description:
      "The account associated with this message is not enabled for toll free messaging",
  },
  "4434": {
    code: "4434",
    name: "Messaging to Toll Free Number Forbidden",
    description:
      "Messaging to this toll free number is not allowed. Number is likely not enabled for messaging or not active.",
  },
  "4435": {
    code: "4435",
    name: "Too Many Recipients",
    description:
      "The group message has too many recipients. When sending Group Messages, there's a maximum of 10 participants in a Group.",
  },
  "4451": {
    code: "4451",
    name: "Invalid User Id",
    description:
      "The user id is not a valid id. Verify the user ID and retry the message",
  },
  "4452": {
    code: "4452",
    name: "Invalid Application ID",
    description:
      "The Application ID specified is not a valid Application Id, or the Application ID is not associated with the account",
  },
  "4470": {
    code: "4470",
    name: "Rejected as SPAM",
    description:
      "This message has been filtered and blocked by Bandwidth for spam. Messages can be blocked for a variety of reason, including but not limited to volumetric filtering, content blocking, SHAFT violation, etc.",
  },
  "4481": {
    code: "4481",
    name: "From Number in black list",
    description:
      "The From number has been flagged by Bandwidth as prohibited from sending messages. This is typically because Bandwidth or a downstream carriers has several violations; reports of spam, P2P violations, associated with this number.",
  },
  "4482": {
    code: "4482",
    name: "To Number in black list",
    description:
      "The number you are attempting to send to is blocked from receiving messages.",
  },
  "4492": {
    code: "4492",
    name: "Message to emergency number forbidden",
    description: "Messaging to an emergency number is forbidden",
  },
  "4493": {
    code: "4493",
    name: "Unauthorized",
    description:
      "Bandwidth service indicates the sender is not authorized to send messages from the account.",
  },
  "4700": {
    code: "4700",
    name: "Carrier Rejected as Invalid Service Type",
    description:
      "Carrier rejected message for invalid service type. This usually means messaging (SMS or MMS) is not supported by the carrier or handset.",
  },
  "4701": {
    code: "4701",
    name: "Destination is not reachable and SMS service is not available.",
    description:
      "Carrier service is reporting the destination is not reachable or the SMS service is not available. ",
  },
  "4702": {
    code: "4702",
    name: "Destination subscriber is unavailable.",
    description:
      "This error indicates the subscriber is unavailable. There are several reasons for this; the subscriber has turned off handset, the destination is unreachable or barred, the GSM subscriber is busy for outbound SMS, SIM card is full, voicemail is full, or cannot reach the destination handset and has stored the message for retry in its « Store & Forward » function. ",
  },
  "4711": {
    code: "4711",
    name: "Media size too large",
    description:
      "Downstream vendor cannot retrieve the media as the MMS attachment is too large",
  },
  "4712": {
    code: "4712",
    name: "The media content type is not supported",
    description:
      "The media content type is not supported. Please review the accepted media types here.",
  },
  "4720": {
    code: "4720",
    name: "Carrier Rejected as Invalid Destination Address",
    description:
      "Carrier Rejected as Invalid Destination Address. This could mean the number is not in the numbering plan (area code does not exist or the number is just invalid) or the number is not enabled for messaging (like a landline). Additionally, for toll free messages to TMobile, this could also mean the user has opted to block all toll free and short code traffic",
  },
  "4721": {
    code: "4721",
    name: "TN on deactivation list",
    description:
      "The phone number you are attempting to send to is on the deactivation list. It is not associated with a carrier to be able to receive messages or is inactive.",
  },
  "4730": {
    code: "4730",
    name: "No route to destination carrier or no roaming route exists.",
    description:
      "Carrier is reporting there is no route available for message. This could be because no routing exists to destination, no roaming route is available, the destination handset is roaming on a network that cannot be reached, no SS7 route, or routing was denied",
  },
  "4740": {
    code: "4740",
    name: "Carrier Rejected as Invalid Source Address",
    description:
      "Carrier is rejecting the message due to invalid source address - the number does not exist in the numbering plan. Other reasons for this error code is the source carrier is invalid or disabled or source not authorized or the number type is not supported.",
  },
  "4750": {
    code: "4750",
    name: "Carrier Rejected Message",
    description:
      "The destination carrier has rejected the message but provided no specific reason. For AT&T traffic, this could be a prepaid user whose account is out of money, a subscriber that is provisioned to not receive this type of SMS or it was identified as Spam",
  },
  "4751": {
    code: "4751",
    name: "Message is too long or message length is invalid for the carrier.",
    description:
      "Carrier has rejected for message length is invalid or too long.",
  },
  "4752": {
    code: "4752",
    name: "Message is malformed for the carrier.",
    description:
      "Carrier is rejecting the message malformed; this could be because of a blank message, unacceptable data value, the receiving SMSC or SME does not accept messages with more than 160 characters, syntax error, content is invalid, message ID is invalid, invalid parameter length, expected TLV missing, invalid TLV value, invalid data coding scheme, invalid number of destinations, error in the optional part of the PDU body, TLV not allowed, or XML validation error.",
  },
  "4753": {
    code: "4753",
    name: "The destination handset has rejected the message",
    description: "The handset has rejected the message",
  },
  "4770": {
    code: "4770",
    name: "Carrier Rejected as SPAM",
    description:
      "The Carrier is reporting this message as blocked for SPAM. Spam blocks could be a result of content, SHAFT violations (including specific keywords), originating address has been flagged for repeated spam content",
  },
  "4771": {
    code: "4771",
    name: "Rejected due to shortened url",
    description:
      "There was an error with the shortened URL used. Bandwidth recommends customers obtain their own dedicated domain if shortened links are needed for their messaging campaign.",
  },
  "4772": {
    code: "4772",
    name: "Blocked sender or receiver",
    description:
      "This error indicates a blocked Sender or Receiver on the downstream platform. Please reach out to Bandwidth support so we can engage our vendor to determine which telephone number is blocked and why.",
  },
  "4773": {
    code: "4773",
    name: "Campaign inactive for destination",
    description:
      "The campaign this TN is assigned to is not active for the destination. Please check the status with TCR (The Campaign Registry). You will receive this error if the campaign is pending or rejected by the DCA or suspended by an MNO but the TN is still assigned to the campaign.",
  },
  "4774": {
    code: "4774",
    name: "Issue with TN provisioning in industry database",
    description:
      "There is an issue with how the number is provisioned in the industry's database. There may be some components of 10DLC provisioning that are incorrect or missing. Please reach out to Bandwidth support to investigate and correct this issue.",
  },
  "4775": {
    code: "4775",
    name: "Carrier Rejected due to user Opt Out",
    description:
      "User has opted out of receiving messages from a particular sender. Remove the destination TN from subscriber list and cease communication with the destination.",
  },
  "4780": {
    code: "4780",
    name: "T-Mobile rejected due to volumetric violation",
    description:
      "T-Mobile rejected due to volumetric violation. You have sent over the daily limit for your 10DLC Brand. Please review your Brand daily throughput limit to ensure you are not exceeding the approved volumes. To improve your Brand score please see our article on external vetting Brand Vetting.",
  },
  "4781": {
    code: "4781",
    name: "AT&T rejected due to 10DLC volumetric violation or throttling",
    description:
      "AT&T rejected due to volumetric violation. You have sent over the rate limit for your 10DLC campaign. Please review your campaign throughput limit to ensure you are not exceeding the approved volumes. This error can also indicate throttling by AT&T for other reasons, including high spam rates.",
  },
  "4785": {
    code: "4785",
    name: "Carrier rejected due to volumetric violation",
    description:
      "The carrier rejected the message due to a volumetric violation. You have sent over the allotted limit and need to back off sending. Please retry after some time.",
  },
  "4790": {
    code: "4790",
    name: "Carrier Rejected Due to Short Code Restriction",
    description:
      "Carrier Rejected Due to Short Code Restriction. Destination address blocked by mobile operator, destination cannot receive short code messages, or the mobile operator blocked the destination from receiving messages from this short code for some other reason.",
  },
  "4791": {
    code: "4791",
    name: "Carrier Rejected Short Code Campaign Not Allowed",
    description:
      "Carrier Rejected SC Campaign Not Allowed or blocked by the mobile operator",
  },
  "4792": {
    code: "4792",
    name: "Carrier Rejected Short Code Not Provisioned",
    description: "Short Code not provisioned on mobile operator's network.",
  },
  "4793": {
    code: "4793",
    name: "Carrier Rejected Short Code Expired",
    description: "Short Code expired with the mobile operator",
  },
  "4795": {
    code: "4795",
    name: "Toll Free number is not verified",
    description:
      "The message was blocked due to the toll free number not being verified. This can also be because there is SPAM on the unverified TFN. Please review unverified sending limits and submit TFN for verification as soon as possible.",
  },
  "5100": {
    code: "5100",
    name: "Application Error",
    description:
      "An application within the Bandwidth service is experiencing a temporary error that is preventing the message from being processed.",
  },
  "5101": {
    code: "5101",
    name: "Application Error",
    description:
      "App going down. Message not received. Sender should send this messages later or to other host.",
  },
  "5106": {
    code: "5106",
    name: "Impossible to route / Attempt to deliver through retries has failed.",
    description:
      "Impossible to route / Attempt to deliver through retries has failed.",
  },
  "5111": {
    code: "5111",
    name: "Application Error",
    description: "Received messaged for connection which is already removed.",
  },
  "5201": {
    code: "5201",
    name: "Application Error",
    description:
      "Bandwidth service expired the message after attempts to deliver through retries failed.",
  },
  "5211": {
    code: "5211",
    name: "Application Error",
    description:
      "Bandwidth service application is temporarily busy so it cannot receive messages at this time",
  },
  "5220": {
    code: "5220",
    name: "Application Error",
    description: "Message not received. Cannot save message to store.",
  },
  "5231": {
    code: "5231",
    name: "Application Error",
    description:
      "Bandwidth did not receive all parts of message. Message can not be sent.",
  },
  "5500": {
    code: "5500",
    name: "General Message Send Failure",
    description:
      "The destination carrier has reported a general service failure with sending the message.",
  },
  "5501": {
    code: "5501",
    name: "General Message Send Failure",
    description:
      "The message is unable to send as no destination is available.",
  },
  "5600": {
    code: "5600",
    name: "Carrier Service Unavailable",
    description:
      "Carrier Service Unavailable. This could result from network congestion, messaging queue full on the vendor side, throttling error on the vendor side.",
  },
  "5610": {
    code: "5610",
    name: "Carrier Service Failure",
    description:
      "The downstream carrier application is experiencing an error. submitting the message has failed or cancelling message has failed",
  },
  "5620": {
    code: "5620",
    name: "Carrier Application Error",
    description:
      "The carrier is reporting a general error associated with their application processing the message.",
  },
  "5630": {
    code: "5630",
    name: "Carrier Application Error",
    description:
      "NACK - no response or acknowledgement received from the carrier",
  },
  "5650": {
    code: "5650",
    name: "Carrier Service Failure",
    description:
      "Carrier Service is reporting a failure to send to destination (mobile operator or handset).",
  },
  "5999": {
    code: "5999",
    name: "Unknown error from Bandwidth",
    description:
      "Unknown error generated by Bandwidth when Bandwidth core reports an unknown error",
  },
  "9902": {
    code: "9902",
    name: "Timed out waiting for delivery receipt. The reason a delivery receipt was not received is not known.",
    description:
      "Bandwidth timed out waiting for the delivery receipt, this could be because the downstream provider did not send the requested delivery receipt or they sent after the system timed out at two hours.",
  },
  "9999": {
    code: "9999",
    name: "Unknown error from downstream. Carrier reported a failure code that is unknown to Bandwidth.",
    description:
      "Bandwidth does not recognize the vendor's error response or does not have the vendor code mapped internally",
  },
};

export default {
  ...codes,
  ...twilioCodes,
};
